import React, { useState, useEffect, useCallback } from 'react';
import {
	ApButtonIcon,
	ApIcon,
	ApRadio,
	colors,
} from '@alixpartners/ui-components';

import { getScrollbarWidth, sortData, TABLE_COLS } from './utils';

import {
	Wrapper,
	Table,
	TableHeaderCell,
	WaveTableHeaderCell,
	TableCell,
	WaveTableCell,
	TableRow,
	TableFooter,
	TableScrollContainer,
	AllocatedIcon,
	FooterTotalsSummary,
	FooterTotals,
	FooterTotalItem,
	NameWrapper,
	HeaderNameWrapper,
} from './InitiativesWavesList.styles';
import { SearchMenu } from 'TaxonomyMapper/SearchMenu';
import { useWindowResize } from 'utils/useWindowResize';

export const InitiativesWavesList = props => {
	let {
		initiatives,
		waves,
		wavesCount,
		initiativesCount,
		currencySymbol,
		assignInitiativeToWave,
		deleteWave,
	} = props;
	const [searchResetKey, setSearchResetKey] = useState(1);
	const [highlightId, setHighlightId] = useState(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState();
	const [sorting, setSorting] = useState({ column: 'name', ascending: false });
	const windowSizes = useWindowResize();
	const [sortedData, setSortedData] = useState(sortData(initiatives, sorting));
	useEffect(
		() => {
			setSortedData(sortData(initiatives, sorting));
		},
		[sorting, initiatives]
	);
	const windowHeight = windowSizes[1];
	const minRows = Math.ceil((windowHeight - 190) / 36);
	const noScroll = sortedData.length < minRows;
	const scrollBarWidth = noScroll ? 0 : getScrollbarWidth();

	const handleSearch = id => {
		setHighlightId(id);
		scrollToElement(id);
	};

	const scrollToElement = id => {
		const el = document.getElementById(`TableRow${id}`);

		// Scroll to selected item if container has scroll
		if (el) {
			el.scrollIntoView({
				block: 'end',
				inline: 'nearest',
			});
		}
	};

	const clearSearchQuery = useCallback(() => {
		setSearchResetKey(Math.round(Math.random() * 100));
		setHighlightId(null);
	}, []);

	useEffect(
		() => {
			if (highlightId) {
				document.addEventListener('click', clearSearchQuery, true);
			} else {
				document.removeEventListener('click', clearSearchQuery, true);
			}

			return () => {
				document.removeEventListener('click', clearSearchQuery, true);
			};
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[highlightId]
	);

	return (
		<Wrapper>
			<Table
				colsCount={TABLE_COLS.length}
				wavesLength={waves.length}
				style={{ paddingRight: scrollBarWidth }}
			>
				<TableRow>
					{TABLE_COLS.map((column, index) => (
						<TableHeaderCell
							key={column.id}
							onClick={() =>
								setSorting({
									column: column.id,
									ascending:
										sorting.column === column.id ? !sorting.ascending : true,
								})
							}
						>
							{index === 0 ? (
								<HeaderNameWrapper>{column.title} </HeaderNameWrapper>
							) : (
								<span>{column.title}</span>
							)}
							{column.id === sorting.column && (
								<ApIcon
									iconName={
										sorting.ascending ? 'arrow_upward' : 'arrow_downward'
									}
									iconColor={colors.grey4}
									iconSize={16}
								/>
							)}
						</TableHeaderCell>
					))}
					{waves.map((wave, index) => (
						<WaveTableHeaderCell key={wave.id}>
							<span>{waves.length > 4 ? wave.name.substr(-2) : wave.name}</span>
							{!!index &&
								waves.length - 1 === index && (
									<ApButtonIcon
										onClick={() => deleteWave(wave)}
										iconName="baseline_close"
										iconProps={{
											iconSize: 12,
										}}
									/>
								)}
						</WaveTableHeaderCell>
					))}
				</TableRow>
			</Table>
			<TableScrollContainer noScroll={noScroll}>
				<Table colsCount={TABLE_COLS.length} wavesLength={waves.length}>
					{sortedData.map((initiative, index) => (
						<TableRow
							key={initiative.id}
							onMouseMove={() => {
								if (hoveredRowIndex !== index) {
									setHoveredRowIndex(index);
								}
							}}
							isHovered={hoveredRowIndex === index}
							isHighlighted={highlightId && highlightId === initiative.id}
							isAllocated={!!initiative.waveId}
						>
							<TableCell id={`TableRow${initiative.id}`}>
								<AllocatedIcon /> <NameWrapper>{initiative.name}</NameWrapper>
							</TableCell>
							<TableCell>
								{initiative.spendType ? 'Indirect' : 'Direct'}
							</TableCell>
							<TableCell>{initiative.type}</TableCell>
							<TableCell>{initiative.low}</TableCell>
							<TableCell>{initiative.high}</TableCell>
							{waves.map(wave => (
								<WaveTableCell key={wave.id}>
									<ApRadio
										name={`waves${initiative.id}`}
										theme="light"
										type="radio"
										defaultChecked={initiative.waveId === wave.id}
										onChange={() => assignInitiativeToWave(initiative, wave)}
									/>
								</WaveTableCell>
							))}
						</TableRow>
					))}
					{sortedData.length < minRows &&
						Array(minRows - sortedData.length)
							.fill(0)
							.map((_0, index) => (
								<TableRow key={index} isHighlighted={highlightId && false}>
									<TableCell />
									<TableCell />
									<TableCell />
									<TableCell />
									<TableCell />
									{waves.map(wave => <WaveTableCell key={wave.id} />)}
								</TableRow>
							))}
				</Table>
			</TableScrollContainer>
			<TableFooter style={{ paddingRight: scrollBarWidth }}>
				<SearchMenu
					onSearch={handleSearch}
					offsetRight={-92}
					width={420}
					options={initiatives}
					currencySymbol={currencySymbol}
					fieldOnly
					placeholder="Search"
					menuDirection="up"
					key={searchResetKey}
				/>
				<FooterTotalsSummary>{`${wavesCount.reduce(
					(a, b) => a + b
				)}/${initiativesCount} initiatives allocated`}</FooterTotalsSummary>
				<FooterTotals wavesLength={waves.length}>
					{waves.map(wave => (
						<FooterTotalItem key={wave.id}>
							{wavesCount[wave.id] || '0'}
						</FooterTotalItem>
					))}
				</FooterTotals>
			</TableFooter>
		</Wrapper>
	);
};

export default InitiativesWavesList;
